import { Preview } from "@creatomate/preview";
import { ElementSource, Story, Template } from "chq";
import { getImageFromStory } from "app/utils";
import imagesSizes from "constants/imagesSizes";

import { flattenMultiImageStories } from "components/stories/video-studio/utils";
import { ELEMENT_NAMES, IMAGE_SLIDE_DURATION, IMAGE_TYPE, VIDEO_TYPE } from "components/stories/video-studio/utils/constants";
import { ImplementorMontageTemplateParams } from "components/stories/video-studio/utils/creatomate/setMontageCustomTemplate";
import createElement from "components/stories/video-studio/utils/creatomate/createElement";
import findElementByName from "../../utils/findElementByName";

export const createSantander4_5wRedLogoOutro = async (
  preview: Preview,
  template: Template,
  story: Story,
  storyDuration: number,
  time: number
): Promise<string | undefined> => {
  const { creator, media } = story;
  const { mediaType, url } = media;

  const json = preview.getSource();
  const elements = json?.elements;
  const nameElement = findElementByName(elements, ELEMENT_NAMES.NAME);
  const NAME_TIME_TO_DISPLAY = nameElement?.time ?? 0;
  const NAME_DURATION = nameElement?.duration;

  // create name
  await createElement({
    text: creator.name,
    time: time + NAME_TIME_TO_DISPLAY,
    duration:
    storyDuration < (NAME_DURATION + NAME_TIME_TO_DISPLAY)
      ? storyDuration - NAME_TIME_TO_DISPLAY
      : NAME_DURATION,
    ...template.commonProperties?.name
  }, preview);

  // create title
  await createElement({
    text: creator.title,
    time: time + NAME_TIME_TO_DISPLAY,
    duration:
      storyDuration < (NAME_DURATION + NAME_TIME_TO_DISPLAY)
        ? storyDuration - NAME_TIME_TO_DISPLAY
        : NAME_DURATION,
    ...template.commonProperties?.title
  }, preview);

  // name_and_title_overlay
  await createElement({
    time: time + NAME_TIME_TO_DISPLAY,
    duration: storyDuration < (NAME_DURATION + NAME_TIME_TO_DISPLAY)
      ? storyDuration - NAME_TIME_TO_DISPLAY
      : NAME_DURATION,
    ...template.commonProperties?.name_and_title_overlay
  }, preview);

  // create story media
  return createElement({
    name: `${creator.name}'s ${mediaType}`,
    type: mediaType,
    source:
      mediaType === VIDEO_TYPE
        ? url
        : getImageFromStory(imagesSizes.FULL, story),
    duration: storyDuration,
    time,
    ...template.commonProperties?.media
  }, preview);
};

const Santander4_5wRedLogoOutro = async ({
  template,
  preview,
  selectedStories,
  onStoryUpdate
}: ImplementorMontageTemplateParams): Promise<void> => {
  const json = preview.getSource();
  const elements = json?.elements;
  const nameElement = findElementByName(elements, ELEMENT_NAMES.NAME);
  const storyElement = findElementByName(elements, ELEMENT_NAMES.STORY);
  const redLogoOutroElement = findElementByName(elements, ELEMENT_NAMES.RED_LOGO_OUTRO);

  const LOGO_OUTRO_DURATION = redLogoOutroElement?.duration ?? 0;
  const NAME_TIME_TO_DISPLAY = nameElement?.time ?? 0;
  const NAME_DURATION = nameElement?.duration;
  const ANIMATION_TIME_OUTRO = (storyElement?.duration - redLogoOutroElement?.time);

  const nameElements = [
    findElementByName(elements, ELEMENT_NAMES.NAME)
  ];
  const titleElements = [
    findElementByName(elements, ELEMENT_NAMES.TITLE)
  ];
  const storyElements = [
    findElementByName(elements, ELEMENT_NAMES.STORY)
  ];
  const nameTitleOverlayElements = [
    findElementByName(elements, ELEMENT_NAMES.NAME_AND_TITLE_OVERLAY)
  ];

  const deleteStoryByIndex = async (index: number): Promise<void> => {
    const json = preview.getSource();
    const { elements } = json;
    let newElements = [...elements];
    newElements = newElements.filter((element: ElementSource) => (
      element.id !== nameElements[index]?.id
      && element.id !== nameElements[index]?.id
      && element.id !== titleElements[index]?.id
      && element.id !== nameTitleOverlayElements[index]?.id
      && element.id !== storyElements[index]?.id
    ));
    return preview.setSource({ ...json, elements: newElements });
  };

  const flattenStories = flattenMultiImageStories(selectedStories);
  let modifications = {};
  let durationModifications = {};
  let nameModifications = {};
  let time = 0;

  for (let index = 0; index < flattenStories.length; index += 1) {
    const story = flattenStories[index];
    const { creator, media } = story;
    const { mediaType, url, duration = 0 } = media;
    // eslint-disable-next-line max-len
    const storyDuration = mediaType === IMAGE_TYPE ? IMAGE_SLIDE_DURATION : duration;

    if (index < 1) {
      durationModifications = { ...durationModifications, [`${storyElements[index]?.id}.duration`]: storyDuration };
      modifications = { ...modifications,
        [`${storyElements[index]?.id}`]: mediaType === VIDEO_TYPE ? url : getImageFromStory(imagesSizes.FULL, story),
        [`${storyElements[index]?.id}.type`]: mediaType,
        [`${storyElements[index]?.id}.time`]: time,
        [`${nameElements[index]?.id}`]: creator.name,
        [`${nameElements[index]?.id}.time`]: time + NAME_TIME_TO_DISPLAY,
        [`${nameTitleOverlayElements[index]?.id}.time`]: time + NAME_TIME_TO_DISPLAY,
        [`${titleElements[index]?.id}`]: creator.title || "",
        [`${titleElements[index]?.id}.time`]: time + NAME_TIME_TO_DISPLAY
      };

      if (storyDuration < NAME_DURATION + NAME_TIME_TO_DISPLAY) {
        modifications = { ...modifications,
          [`${nameElements[index]?.id}.duration`]: storyDuration - NAME_TIME_TO_DISPLAY,
          [`${titleElements[index]?.id}.duration`]: storyDuration - NAME_TIME_TO_DISPLAY,
          [`${nameTitleOverlayElements[index]?.id}.duration`]: storyDuration - NAME_TIME_TO_DISPLAY
        };
      }
      nameModifications = { ...nameModifications, [`${storyElements[index]?.id}.name`]: story.media.mediaType === VIDEO_TYPE ? `${creator.name}'s video` : `${creator.name}'s image` };
      onStoryUpdate({ ...story, creatomateId: storyElements[index]?.id });
    } else {
      // eslint-disable-next-line no-await-in-loop
      const elementId = await createSantander4_5wRedLogoOutro(
        preview, template, story, storyDuration, time
      );
      onStoryUpdate({ ...story, creatomateId: elementId });
    }
    // story duration - animation (1 second per transition)
    time += storyDuration;
  }

  modifications = { ...modifications, [`${redLogoOutroElement?.id}.time`]: time };
  if (flattenStories.length < 1) {
    for (let index = flattenStories.length; index < 1; index += 1) {
      // eslint-disable-next-line no-await-in-loop
      await deleteStoryByIndex(index);
    }
  }
  // Plus outro
  modifications = { ...modifications,
    duration: time + LOGO_OUTRO_DURATION
    - ANIMATION_TIME_OUTRO
  };
  await preview.applyModifications({ ...modifications, ...durationModifications });
  await preview.applyModifications(nameModifications);
};

export default Santander4_5wRedLogoOutro;
